import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://automatchlab.com';

function useFadeUp(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVisible(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return { ref, visible };
}

function WhyCard({ icon, title, desc, index }: { icon: string; title: string; desc: string; index: number }) {
  const { ref, visible } = useFadeUp(index * 80);
  return (
    <div ref={ref} className="rounded-2xl p-6" style={{ background: 'rgba(196,168,130,0.04)', border: '1px solid rgba(196,168,130,0.1)', opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transition: `opacity 0.6s cubic-bezier(.22,1,.36,1) ${index * 60}ms, transform 0.6s cubic-bezier(.22,1,.36,1) ${index * 60}ms` }}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(196,168,130,0.09)', border: '1px solid rgba(196,168,130,0.18)' }}>
        <i className={`${icon} text-xl`} style={{ color: '#c4a882' }}></i>
      </div>
      <h3 className="text-sm font-bold text-white mb-2">{title}</h3>
      <p className="text-xs leading-relaxed" style={{ color: 'rgba(196,168,130,0.5)' }}>{desc}</p>
    </div>
  );
}

function ProcessStep({ icon, title, desc, index, isLast }: { icon: string; title: string; desc: string; index: number; isLast: boolean }) {
  const { ref, visible } = useFadeUp(index * 80);
  return (
    <div ref={ref} className="flex gap-5" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(28px)', transition: 'opacity 0.6s cubic-bezier(.22,1,.36,1), transform 0.6s cubic-bezier(.22,1,.36,1)' }}>
      <div className="flex flex-col items-center gap-2 flex-shrink-0">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm" style={{ background: 'rgba(196,168,130,0.12)', border: '1px solid rgba(196,168,130,0.25)', color: '#c4a882', fontFamily: 'Satoshi, Inter, sans-serif' }}>{index + 1}</div>
        {!isLast && <div className="flex-1 w-px" style={{ background: 'rgba(196,168,130,0.12)', minHeight: '32px' }}></div>}
      </div>
      <div className="pb-8">
        <div className="flex items-center gap-2 mb-2">
          <i className={`${icon} text-lg`} style={{ color: '#c4a882' }}></i>
          <h3 className="text-base sm:text-lg font-black text-white" style={{ fontFamily: 'Satoshi, Inter, sans-serif' }}>{title}</h3>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: 'rgba(196,168,130,0.55)' }}>{desc}</p>
      </div>
    </div>
  );
}

const process = {
  en: [
    { icon: 'ri-video-chat-line', title: '60-Min Virtual Consultation', desc: 'We confirm your exact vehicle choice, preferred specs, budget ceiling, timeline, and any deal-breakers. Everything is documented before we start.' },
    { icon: 'ri-search-2-line', title: 'Market-Wide Vehicle Search', desc: 'We scour every dealership network and private seller platform in your region — new and used — to find every matching unit available.' },
    { icon: 'ri-filter-line', title: 'Vetting & Shortlisting', desc: 'Each candidate vehicle is checked against your specs, history reports pulled, and dealer reputation assessed before you see a single listing.' },
    { icon: 'ri-phone-line', title: 'Dealer & Seller Negotiations', desc: 'Our team contacts dealers and private sellers on your behalf, negotiates price, and pushes for the best deal. You never have to talk to a salesperson.' },
    { icon: 'ri-file-text-line', title: 'Finance & Lease Structuring', desc: 'We break down every financing and lease option clearly — rates, terms, total cost — so you choose the structure that actually works for you.' },
    { icon: 'ri-coupon-line', title: 'Incentives & Rebates Applied', desc: 'We ensure every federal, provincial, and manufacturer incentive is applied to your deal. Not a single dollar left on the table.' },
    { icon: 'ri-car-wash-line', title: 'Pre-Purchase Inspection', desc: 'A certified mechanic inspects your vehicle before any paperwork is signed. We coordinate the appointment and review the report with you.' },
    { icon: 'ri-truck-line', title: 'Delivery & Logistics', desc: 'We manage the pickup or delivery logistics, ensure all paperwork is in order, and confirm the transaction is clean from end to end.' },
    { icon: 'ri-customer-service-2-line', title: 'Post-Purchase Follow-Up', desc: 'Our job doesn\'t end at delivery. We follow up to ensure everything is as promised and answer any questions that come up after you\'re on the road.' },
  ],
  fr: [
    { icon: 'ri-video-chat-line', title: 'Consultation virtuelle de 30 min', desc: 'Nous confirmons votre choix de véhicule exact, les spécifications préférées, le budget maximum, le calendrier et vos critères non négociables. Tout est documenté avant de commencer.' },
    { icon: 'ri-search-2-line', title: 'Recherche sur tout le marché', desc: 'Nous passons au crible chaque réseau de concessionnaires et plateforme de particuliers dans votre région — neuf et occasion — pour trouver chaque unité disponible.' },
    { icon: 'ri-filter-line', title: 'Vérification et sélection', desc: 'Chaque véhicule candidat est vérifié par rapport à vos critères, les rapports d\'historique obtenus et la réputation du vendeur évaluée avant que vous voyiez une annonce.' },
    { icon: 'ri-phone-line', title: 'Négociation avec les vendeurs', desc: 'Notre équipe contacte les concessionnaires et vendeurs privés en votre nom, négocie le prix et obtient la meilleure offre. Vous n\'avez jamais à parler à un vendeur.' },
    { icon: 'ri-file-text-line', title: 'Structuration financement/location', desc: 'Nous décomposons clairement chaque option de financement et de location — taux, modalités, coût total — pour que vous choisissiez la structure qui vous convient.' },
    { icon: 'ri-coupon-line', title: 'Incitatifs et rabais appliqués', desc: 'Nous nous assurons que chaque incitatif fédéral, provincial et fabricant est appliqué à votre transaction. Pas un dollar laissé de côté.' },
    { icon: 'ri-car-wash-line', title: 'Inspection avant achat', desc: 'Un mécanicien certifié inspecte votre véhicule avant la signature de tout document. Nous coordonnons le rendez-vous et examinons le rapport avec vous.' },
    { icon: 'ri-truck-line', title: 'Livraison et logistique', desc: 'Nous gérons la logistique de collecte ou de livraison, nous assurons que tous les documents sont en ordre et confirmons que la transaction est propre de bout en bout.' },
    { icon: 'ri-customer-service-2-line', title: 'Suivi post-achat', desc: 'Notre travail ne s\'arrête pas à la livraison. Nous effectuons un suivi pour nous assurer que tout est conforme et répondons aux questions après votre prise en main.' },
  ],
};

const whyUs = {
  en: [
    { icon: 'ri-user-unfollow-line', title: 'No Salespeople', desc: 'We work for you — not the dealer. Zero commission pressure, zero upselling.' },
    { icon: 'ri-time-line', title: 'Save 20+ Hours', desc: 'Skip the research, the visits, the back-and-forth. We handle it all.' },
    { icon: 'ri-hand-coin-line', title: 'Save Real Money', desc: 'Our negotiations and incentive expertise typically save clients more than our fee — on the very first deal.' },
    { icon: 'ri-eye-line', title: 'Full Transparency', desc: 'You see every option, every cost, every trade-off. Nothing is hidden, ever.' },
  ],
  fr: [
    { icon: 'ri-user-unfollow-line', title: 'Aucun vendeur', desc: 'Nous travaillons pour vous — pas pour le concessionnaire. Zéro pression de commission, zéro vente incitative.' },
    { icon: 'ri-time-line', title: '20+ heures économisées', desc: 'Oubliez la recherche, les visites, les allers-retours. Nous gérons tout.' },
    { icon: 'ri-hand-coin-line', title: 'Économies réelles', desc: 'Nos négociations et expertise en incitatifs permettent généralement d\'économiser plus que nos honoraires — dès la première transaction.' },
    { icon: 'ri-eye-line', title: 'Transparence totale', desc: 'Vous voyez chaque option, chaque coût, chaque compromis. Rien n\'est caché, jamais.' },
  ],
};

export default function ExpertSourcingPage() {
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const heroFade = useFadeUp(0);
  const priceFade = useFadeUp(200);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  const tx = {
    en: {
      navBack: 'Back',
      eyebrow: 'Expert Sourcing — $149 + $149',
      headline: 'Expert Sourcing',
      sub: 'Once you know what you want — or once we help you find it — we handle every step from search to delivery. You show up, sign, and drive.',
      processTitle: 'Our Full Process',
      processSub: 'Nine steps, zero headaches. We manage everything so you never have to deal with a dealership.',
      whyTitle: 'Why Choose Our Service?',
      whySub: 'We\'re the only people in the room who are 100% on your side.',
      price: '$149',
      priceLabel: 'per service',
      priceNote: 'Includes a 60-min virtual consultation, full search & negotiation, inspection coordination, and post-purchase follow-up.',
      ctaTitle: 'Ready to Start?',
      ctaSub: 'Reach out and we\'ll confirm your vehicle details and get the process moving immediately.',
      ctaBtn: 'Start Sourcing Process',
      matchmaking: 'Matchmaking Platform',
    },
    fr: {
      navBack: 'Retour',
      eyebrow: 'Le Service Clé en Main — 149$ + 149$',
      headline: 'Acquisition Professionnelle',
      sub: 'Une fois que vous savez ce que vous voulez — ou une fois que nous vous aidons à le trouver — nous gérons chaque étape de la recherche à la livraison. Vous arrivez, signez et conduisez.',
      processTitle: 'Notre processus complet',
      processSub: 'Neuf étapes, zéro maux de tête. Nous gérons tout pour que vous n\'ayez jamais à traiter avec un concessionnaire.',
      whyTitle: 'Pourquoi choisir notre service?',
      whySub: 'Nous sommes les seuls dans la pièce qui sont à 100% de votre côté.',
      price: '149$',
      priceLabel: 'par service',
      priceNote: 'Comprend une consultation virtuelle de 60 min, recherche complète et négociation, coordination de l\'inspection et suivi post-achat.',
      ctaTitle: 'Prêt à commencer?',
      ctaSub: 'Contactez-nous et nous confirmerons les détails de votre véhicule et lancerons le processus immédiatement.',
      ctaBtn: 'Réserver le Service Clé en Main',
      matchmaking: 'Plateforme Matchmaking',
    },
  }[lang];

  const seo = {
    en: {
      title: 'Expert Sourcing – Automatchlab',
      description: 'Automatchlab\'s Expert Sourcing service. We find, negotiate, inspect, and deliver your exact vehicle. No dealership visits, no back-and-forth, no wasted time.',
    },
    fr: {
      title: 'Service Clé en Main – Approvisionnement Expert | Automatchlab',
      description: 'Le service d\'approvisionnement expert d\'Automatchlab. Nous trouvons, négocions, inspectons et livrons votre véhicule. Sans visite chez le concessionnaire, sans délai.',
    },
  }[lang];

  return (
    <>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={`${siteUrl}/end-to-end-service`} />
      <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'Service', name: 'Expert Sourcing Service', provider: { '@type': 'Organization', name: 'Automatchlab' }, description: 'Complete vehicle sourcing service: search, negotiate, inspect, and deliver any vehicle in Canada.', url: `${siteUrl}/end-to-end-service`, offers: { '@type': 'Offer', price: '149', priceCurrency: 'CAD', description: '$149 upfront + $149 after delivery' } })}</script>

      <div className="min-h-screen" style={{ background: '#06060e', fontFamily: 'Satoshi, Inter, sans-serif' }}>
        {/* Nav */}
        <nav className="sticky top-0 z-50 h-14 sm:h-16 flex items-center" style={{ background: 'rgba(6,6,14,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(196,168,130,0.12)' }}>
          <div className="w-full px-4 sm:px-8 lg:px-20 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap" style={{ color: 'rgba(196,168,130,0.7)' }}>
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-left-line text-base"></i></div>
                {tx.navBack}
              </button>
              <div className="w-px h-4" style={{ background: 'rgba(196,168,130,0.2)' }}></div>
              <Link to="/"><img src="https://static.readdy.ai/image/b7d981aa05e2e3cec9e239b766e600d9/820f44dc42d7692bb1a0c21b796d9fab.svg" alt="Automatchlab" className="h-7 sm:h-9 w-auto object-contain" /></Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/matchmaking-platform" className="hidden sm:flex items-center gap-1.5 text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer" style={{ color: 'rgba(196,168,130,0.6)' }}>
                <i className="ri-search-eye-line"></i>{tx.matchmaking}
              </Link>
              <div className="flex items-center gap-0.5 rounded-lg p-0.5" style={{ background: 'rgba(196,168,130,0.08)', border: '1px solid rgba(196,168,130,0.15)' }}>
                <button onClick={() => setLang('en')} className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${lang === 'en' ? 'text-[#0e0b07]' : 'text-[#c4a882]/60 hover:text-[#c4a882]'}`} style={lang === 'en' ? { background: '#c4a882' } : {}}>EN</button>
                <button onClick={() => setLang('fr')} className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${lang === 'fr' ? 'text-[#0e0b07]' : 'text-[#c4a882]/60 hover:text-[#c4a882]'}`} style={lang === 'fr' ? { background: '#c4a882' } : {}}>FR</button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="relative overflow-hidden py-16 sm:py-24 lg:py-32 px-4 sm:px-8 lg:px-20">
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(196,168,130,0.05) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(196,168,130,0.06) 0%, transparent 60%)' }} />
          <div ref={heroFade.ref} className="relative z-10 max-w-4xl mx-auto text-center" style={{ opacity: heroFade.visible ? 1 : 0, transform: heroFade.visible ? 'translateY(0)' : 'translateY(32px)', transition: 'opacity 0.8s cubic-bezier(.22,1,.36,1), transform 0.8s cubic-bezier(.22,1,.36,1)' }}>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-5 sm:mb-7" style={{ letterSpacing: '-0.02em' }}>
              {tx.headline}
            </h1>
            <p className="text-base sm:text-xl leading-relaxed max-w-2xl mx-auto mb-8 sm:mb-12" style={{ color: 'rgba(196,168,130,0.65)' }}>
              {tx.sub}
            </p>

            {/* Price card */}
            <div ref={priceFade.ref} className="inline-flex flex-col items-center gap-2 px-8 py-6 rounded-2xl" style={{ background: 'rgba(196,168,130,0.06)', border: '1px solid rgba(196,168,130,0.18)', opacity: priceFade.visible ? 1 : 0, transform: priceFade.visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s' }}>
              <div className="flex flex-col items-center gap-1.5">
                <div className="flex items-baseline gap-2 justify-center">
                  <span className="text-5xl sm:text-6xl font-black" style={{ color: '#c4a882' }}>{tx.price}</span>
                  <span className="text-sm sm:text-base font-semibold" style={{ color: 'rgba(196,168,130,0.65)' }}>{lang === 'fr' ? "à l'avance" : 'upfront'}</span>
                </div>
                <div className="flex items-baseline gap-2 justify-center">
                  <span className="text-5xl sm:text-6xl font-black" style={{ color: '#c4a882' }}>{tx.price}</span>
                  <span className="text-sm sm:text-base font-semibold" style={{ color: 'rgba(196,168,130,0.65)' }}>{lang === 'fr' ? 'à la livraison' : 'after delivery'}</span>
                </div>
              </div>
              <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'rgba(196,168,130,0.45)' }}>{tx.priceLabel}</span>
              <p className="text-xs sm:text-sm text-center max-w-xs leading-relaxed mt-1" style={{ color: 'rgba(196,168,130,0.45)' }}>{tx.priceNote}</p>
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="py-14 sm:py-20 px-4 sm:px-8 lg:px-20" style={{ background: '#0c0c16' }}>
          <div className="max-w-6xl mx-auto">
            <div className="mb-10 sm:mb-14">
              <h2 className="text-2xl sm:text-4xl font-black text-white mb-3" style={{ letterSpacing: '-0.02em' }}>{tx.whyTitle}</h2>
              <p className="text-sm sm:text-base" style={{ color: 'rgba(196,168,130,0.5)' }}>{tx.whySub}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {whyUs[lang].map((item, i) => (
                <WhyCard key={i} icon={item.icon} title={item.title} desc={item.desc} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section className="py-14 sm:py-20 px-4 sm:px-8 lg:px-20" style={{ background: '#08080f' }}>
          <div className="max-w-3xl mx-auto">
            <div className="mb-10 sm:mb-14">
              <h2 className="text-2xl sm:text-4xl font-black text-white mb-3" style={{ letterSpacing: '-0.02em' }}>{tx.processTitle}</h2>
              <p className="text-sm sm:text-base" style={{ color: 'rgba(196,168,130,0.5)' }}>{tx.processSub}</p>
            </div>
            {process[lang].map((step, i) => (
              <ProcessStep key={i} icon={step.icon} title={step.title} desc={step.desc} index={i} isLast={i === process[lang].length - 1} />
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24 px-4 sm:px-8 lg:px-20 text-center" style={{ background: '#0c0c16' }}>
          <div className="max-w-2xl mx-auto">
            <div className="h-px w-24 mx-auto mb-8" style={{ background: 'linear-gradient(90deg, transparent, #c4a882, transparent)' }}></div>
            <h2 className="text-2xl sm:text-4xl font-black text-white mb-4" style={{ letterSpacing: '-0.02em' }}>{tx.ctaTitle}</h2>
            <p className="text-sm sm:text-base mb-8 sm:mb-10" style={{ color: 'rgba(196,168,130,0.55)' }}>{tx.ctaSub}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="mailto:info@automatchlab.ca" className="flex items-center gap-2 px-8 py-4 rounded-xl font-black text-sm tracking-wide cursor-pointer whitespace-nowrap transition-all duration-200 text-[#0e0b07]" style={{ background: 'linear-gradient(135deg, #c4a882 0%, #a88c6a 100%)' }}>
                <i className="ri-mail-line"></i>
                {tx.ctaBtn}
              </a>
              <button onClick={() => navigate('/matchmaking-platform')} className="flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-sm tracking-wide cursor-pointer whitespace-nowrap transition-all duration-200" style={{ background: 'rgba(196,168,130,0.07)', border: '1px solid rgba(196,168,130,0.22)', color: 'rgba(232,216,192,0.8)' }}>
                <i className="ri-search-eye-line"></i>
                {tx.matchmaking}
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 sm:px-8 lg:px-20 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderTop: '1px solid rgba(196,168,130,0.1)', background: '#06060e' }}>
          <Link to="/"><img src="https://static.readdy.ai/image/b7d981aa05e2e3cec9e239b766e600d9/820f44dc42d7692bb1a0c21b796d9fab.svg" alt="Automatchlab" className="h-7 w-auto object-contain" /></Link>
          <p className="text-xs" style={{ color: 'rgba(196,168,130,0.35)' }}>© 2024 Automatchlab. All rights reserved.</p>
        </footer>
      </div>
    </>
  );
}